export type DownloadProgressStatus = 'idle' | 'starting' | 'downloading' | 'complete' | 'error';

export interface DownloadProgress {
  status: DownloadProgressStatus;
  currentType?: string;
  currentTypeIndex: number;
  totalTypes: number;
  currentTypeDownloaded: number;
  currentTypeTotal: number;
  recordsDownloaded: number;
  totalRecords: number;
  isResuming?: boolean;
  error?: string;
}

const initialDownloadProgress: DownloadProgress = {
  status: 'idle',
  currentTypeIndex: 0,
  totalTypes: 0,
  currentTypeDownloaded: 0,
  currentTypeTotal: 0,
  recordsDownloaded: 0,
  totalRecords: 0,
};

let currentDownloadProgress: DownloadProgress = initialDownloadProgress;
let listeners: Array<(progress: DownloadProgress) => void> = [];

export function getDownloadProgress(): DownloadProgress {
  return currentDownloadProgress;
}

export function setDownloadProgress(progress: Partial<DownloadProgress>) {
  currentDownloadProgress = {
    ...currentDownloadProgress,
    ...progress,
  };

  listeners.forEach(listener => listener(currentDownloadProgress));
}

export function resetDownloadProgress() {
  currentDownloadProgress = initialDownloadProgress;
  listeners.forEach(listener => listener(currentDownloadProgress));
}

export function subscribeToDownloadProgress(listener: (progress: DownloadProgress) => void): () => void {
  listeners.push(listener);
  listener(currentDownloadProgress);

  return () => {
    listeners = listeners.filter(existingListener => existingListener !== listener);
  };
}

export function formatSyncType(type: string | undefined): string {
  if (!type) {
    return 'updates';
  }

  return type
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, character => character.toUpperCase());
}

export function formatProgressNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '0';
  }

  return Math.max(0, Math.floor(value)).toLocaleString();
}

export function formatTotalDownloadProgress(progress: DownloadProgress): string {
  const downloaded = Math.min(progress.recordsDownloaded, progress.totalRecords || progress.recordsDownloaded);
  const label = progress.currentType ? formatSyncType(progress.currentType) : 'Downloaded';
  return `${label} ${formatProgressNumber(downloaded)} / ${formatProgressNumber(progress.totalRecords)}`;
}

export function formatDownloadStatus(progress: DownloadProgress): string {
  if (progress.status === 'starting') {
    if (progress.isResuming) {
      return 'Resuming download';
    }

    return 'Preparing download';
  }

  if (progress.status === 'error') {
    return progress.error || 'Download failed';
  }

  if (progress.status === 'downloading') {
    return formatTotalDownloadProgress(progress);
  }

  if (!progress.currentType) {
    return 'Downloading updates';
  }

  return formatTotalDownloadProgress(progress);
}
